import { AHButton, Box, useLayoutContext } from "arkh";
import React, { useCallback } from "react";

import { useSafeAreaInsets } from "react-native-safe-area-context";

import { FlatList, ListRenderItem, StyleSheet } from "react-native";
import { WidgetSizeIndicator } from "./WidgetSizeIndicator";

import { WASizePreviewViewabilityConfig } from "./WASizePreviewViewabilityConfig";
import { WASizePreviewProps } from "./WASizePreviewProps";
import { useWASizePreviewSize } from "./useWASizePreviewSize";
import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { WAPreviewContainer } from "@/src/features/Storecast/Storecast/WidgetAdd/WAPreviewContainer";
import { widgetAddSizeGreaterOrEqualTo } from "../utils/widgetAddSizeGreaterOrEqualTo";

const PREVIEW_ITEM_COMPACT_HEIGHT = 170;

const styles = StyleSheet.create({
  item: {
    height: PREVIEW_ITEM_COMPACT_HEIGHT,
    width: PREVIEW_ITEM_COMPACT_HEIGHT,
    justifyContent: "center",
    alignItems: "center",
  },
});

export function WASizePreviewCompact(props: WASizePreviewProps) {
  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();
  const intl_token = useIntlTokens();
  const { size, onViewableItemsChanged, scrollIndex } = useWASizePreviewSize({
    widgetSizes: props.widgetSizes,
    maxSize: props.maxSize,
    defaultSize: props.defaultSize,
  });
  const children = props.children;
  const renderItem: ListRenderItem<WidgetSizeT> = useCallback(
    ({ item, index }) => {
      return (
        <Box style={styles.item}>
          <WAPreviewContainer
            h={item.h}
            w={item.w}
            unitSize={70}
            scale={item.h === 3 ? 3 / 4 : item.h === 4 ? 2 / 4 : 1}
          >
            {scrollIndex === index ? (
              <>{typeof children === "function" ? children(item) : children}</>
            ) : null}
          </WAPreviewContainer>
        </Box>
      );
    },
    [scrollIndex, children]
  );

  const getItemLayout = useCallback(
    (_: unknown, index: number) => ({
      index,
      length: PREVIEW_ITEM_COMPACT_HEIGHT,
      offset: index * PREVIEW_ITEM_COMPACT_HEIGHT,
    }),
    []
  );

  return (
    <Box
      style={{
        flexDirection: "row",
        alignItems: "center",
      }}
      insets={{
        bottom: insets.bottom,
      }}
      spaceLeft="medium"
      color="surface-backgroundControl"
    >
      <WidgetSizeIndicator
        vertical
        currentSize={size}
        maxSize={props.maxSize}
        widgetSizes={props.widgetSizes}
      />
      <Box
        style={{
          width: PREVIEW_ITEM_COMPACT_HEIGHT,
          height: PREVIEW_ITEM_COMPACT_HEIGHT,
        }}
      >
        <FlatList
          data={props.widgetSizes}
          pagingEnabled
          scrollEnabled={props.widgetSizes.length > 1}
          viewabilityConfig={WASizePreviewViewabilityConfig}
          onViewableItemsChanged={onViewableItemsChanged}
          getItemLayout={getItemLayout}
          removeClippedSubviews
          initialNumToRender={1}
          showsVerticalScrollIndicator={false}
          renderItem={renderItem}
          initialScrollIndex={scrollIndex}
        />
      </Box>
      <AHButton
        themeType="primary"
        onPress={() => {
          props.onCreate(size);
        }}
        disabled={
          props.disabled || !widgetAddSizeGreaterOrEqualTo(props.maxSize, size)
        }
        style={{
          flex: 1,
          marginHorizontal: layout.space.medium,
        }}
        labelStyle={{
          textAlign: "center",
        }}
        title={
          !widgetAddSizeGreaterOrEqualTo(props.maxSize, size)
            ? intl_token.NotEnoughSpace
            : intl_token.Add
        }
        loading={props.loading}
      />
    </Box>
  );
}
